import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";
import { useAudit } from "@/components/AuditContext";
import { SERVICES } from "@/lib/servicesData";
import { INCLUDED, NETWORK_NOTE } from "@/lib/content";

// Cities with real, hand-written local content. Empty for now on purpose:
// we never auto-generate duplicated local pages (Google penalises this).
const PUBLISHED_CITIES = {};

const Breadcrumb = ({ items }) => (
  <nav aria-label="Fil d'Ariane" className="flex items-center flex-wrap gap-2 text-sm text-white/60">
    {items.map((it, i) => (
      <span key={it.path} className="flex items-center gap-2">
        {i > 0 && <ChevronRight className="w-3.5 h-3.5" />}
        {i < items.length - 1 ? (
          <Link to={it.path} className="hover:text-white transition-colors">{it.name}</Link>
        ) : (
          <span className="text-white">{it.name}</span>
        )}
      </span>
    ))}
  </nav>
);

export default function ServicePage({ slug }) {
  const { ville } = useParams();
  const svc = SERVICES[slug];
  const { openAudit } = useAudit();

  if (!svc) return <Navigate to="/" replace />;
  // Local page URLs are accepted structurally but not yet published.
  if (ville && !PUBLISHED_CITIES[`${slug}/${ville}`]) {
    return <Navigate to={`/${slug}`} replace />;
  }

  const path = `/${slug}`;
  const breadcrumb = [
    { name: "Accueil", path: "/" },
    { name: "Services", path: "/offres" },
    { name: svc.navLabel, path },
  ];

  return (
    <>
     <Seo title={svc.seoTitle} description={svc.seoDescription} path={path} />
      <section className="bg-navy grain relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-[-8%] w-[40rem] h-[40rem] rounded-full bg-amber-500/10 blur-[140px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8">
          <Breadcrumb items={breadcrumb} />
          <Reveal>
            <span className="mt-8 inline-block text-xs uppercase tracking-[0.25em] font-medium text-amber-500">{svc.kicker}</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white max-w-4xl text-balance">{svc.title}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">{svc.intro}</p>
          </Reveal>
        </div>
      </section>

      {svc.promoAudit && (
        <section className="bg-white pt-16 md:pt-20" data-testid="service-promo-audit">
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <div className="relative overflow-hidden rounded-3xl bg-navy grain p-8 sm:p-10 lg:p-12">
              <div className="pointer-events-none absolute -top-16 -right-10 w-[26rem] h-[26rem] rounded-full bg-amber-500/15 blur-[120px]" aria-hidden />
              <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-4 py-1.5">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <span className="text-amber-400 text-sm font-medium">Offre spéciale · l'audit réel</span>
                  </div>
                  <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white text-balance">
                    Passez du ressenti au constat.
                  </h2>
                  <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
                    Les bons réflexes, c'est la base. Mais pour savoir où vous en êtes vraiment, il faut mesurer. À l'aide d'outils professionnels, nous analysons votre réseau et vos postes pour révéler vos failles réelles — puis vous remettons un état des lieux précis et des priorités.
                  </p>
                  <div className="mt-6 flex items-center gap-4 flex-wrap">
                    <span className="font-display text-4xl font-bold text-white tabular-nums">100 €<span className="text-lg text-white/60 font-medium"> HT</span></span>
                    <span className="text-slate-400 text-xl line-through">500 € HT</span>
                    <span className="inline-flex items-center rounded-full bg-amber-600 text-white text-xs font-semibold px-2.5 py-1">−80 %</span>
                  </div>
                  <p className="mt-2 text-slate-400 text-sm">Et 100 € déduits si vous souscrivez ensuite une offre — l'audit peut ne rien vous coûter.</p>
                </div>
                <div className="shrink-0">
                  <Link to="/promo" data-testid="service-promo-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 text-white px-8 py-4 text-base font-medium hover:bg-amber-700 hover:-translate-y-0.5 transition-[transform,background-color] duration-300 shadow-[0_10px_30px_-12px_rgba(217,119,6,0.7)]">
                    Voir l'offre audit <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 space-y-16">
          {svc.sections.map((s, i) => (
            <Reveal key={i}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-10">
                <div className="md:col-span-4">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-navy">{s.heading}</h2>
                </div>
                <div className="md:col-span-8">
                  {s.paragraphs && (
                    <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                      {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
                    </div>
                  )}
                  {s.items && (
                    <ul className="space-y-4">
                      {s.items.map((it) => (
                        <li key={it} className="flex items-start gap-3 text-navy text-lg"><Check className="w-5 h-5 text-amber-600 mt-1 shrink-0" /><span>{it}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Rappel : inclus sans supplément */}
      <section className="bg-[#f8fafc] py-20 md:py-24 border-y border-slate-200">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-1.5">
              <Check className="w-4 h-4 text-amber-600" />
              <span className="text-amber-700 text-sm font-medium">Compris dans les deux formules, sans supplément</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy max-w-3xl text-balance">
              Ce service ne se paie pas à part. Il fait partie de tout compris.
            </h2>
          </Reveal>
          <Stagger className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {INCLUDED.map((it) => (
              <StaggerItem key={it} className="flex items-start gap-3 text-slate-700">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" /><span>{it}</span>
              </StaggerItem>
            ))}
          </Stagger>
          <p className="mt-8 text-slate-500 text-sm leading-relaxed max-w-3xl">{NETWORK_NOTE}</p>
          <Reveal delay={0.1}>
            <Link to="/offres" className="mt-10 inline-flex items-center gap-2 text-navy font-medium link-underline">
              Voir les formules et les tarifs <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy grain relative py-24 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50rem] h-[28rem] rounded-full bg-amber-500/12 blur-[140px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              Un échange gratuit pour faire le point, sans engagement.
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <CTAButton size="lg" withArrow className="mt-10" onClick={openAudit} data-testid="service-audit-cta">
              Réserver un échange gratuit
            </CTAButton>
          </Reveal>
        </div>
      </section>
    </>
  );
}
